import axios from 'axios';
import { aicaBaseUrl, cerberusBaseUrl, GET, polusBaseUrl, POST } from '../../utils/axios-interceptor';
import { getTimeStamp } from '../../utils/dateUtils';
import {
    FetchInvestorConfigPayload,
    FetchMeetingTranscriptPayload,
    FetchOrgBasedUsersPayload,
    FetchPortfolioDataPayload,
    GetOwnerDataPayload,
    InitiateReportGenerationPayload,
    InviteCompanyPayload,
    InviteCompanyPayloadV2,
} from './action';
import { EngagementLetterStatus, MonitoringStatus, RiskGrade } from './constants';
import { Option } from '../../modules/Investor/Deals/components/DealsList/DealsTable/components/LenderUsersCell/LenderUsersCell';
import { SortConfig } from '../../utils/constants/commonConstants';
import { cleanCustomFields } from '../../utils/utils';
import type { CommentEntityType } from '../../modules/Investor/CommentsThread/types';

// getTimeStamp() has only second-level granularity — fine for a single sequential
// request, but multiple calls to the same endpoint firing within the same second
// would otherwise share an identical x-request-id/requestId. Confirmed via live
// testing (back when the Deals/Leads export paged with a concurrent batched
// fetch) that this caused the backend's async request-correlation to
// cross-talk between concurrent calls (one request's rows silently going missing).
// The Deals/Leads export services now page sequentially (never concurrently)
// through these same getPortfolioData/getAllDealsService/etc. functions shared
// with the on-screen table fetches, so the random-suffix uniqueness stays as a
// cheap safety net. Appending a random suffix keeps every call's ID unique
// without touching the many other getTimeStamp() call sites in this file that
// only ever fire one request at a time.
const getUniqueRequestId = () => `${getTimeStamp()}_${Math.random().toString(36).slice(2, 8)}`;

export function getInvestorConfig(payload: FetchInvestorConfigPayload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                accountType: payload.accountType,
                investorOrgId: payload.investorOrgId,
                primaryEmail: payload.primaryEmail,
                assesseeOrgId: payload.assesseeOrgId,
                reportId: payload?.reportId || '',
                configName: payload.configName,
            },
            userRole: payload?.userRole,
        },
    });
}

export function getLenderConfig(payload: FetchInvestorConfigPayload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                accountType: payload.accountType,
                investorOrgId: payload.investorOrgId,
                primaryEmail: payload.primaryEmail,
                assesseeOrgId: payload.assesseeOrgId,
                configName: payload.configName,
            },
            userRole: payload?.userRole,
        },
    });
}

export function getPortfolioData(payload: FetchPortfolioDataPayload) {
    let apipayload: { [key: string]: string | number | object } = {
        portfolioTypeToFetch: payload.portfolioTypeToFetch,
        pageNumber: payload.pageNumber,
        companyAccessType: payload.companyAccessType,
        requestId: getUniqueRequestId(),
    };
    // Only set by the "Export to Excel" paged fetch (leadsExportService.ts) — the
    // on-screen fetch never sends pageSize, so this is a no-op for existing callers.
    if (payload.pageSize) apipayload.pageSize = payload.pageSize;
    if (payload.partnerUserId) apipayload.partnerUserId = payload.partnerUserId;
    if (payload.filters) apipayload.filters = payload.filters;
    if (payload.sortConfig?.direction) apipayload.sortConfig = payload.sortConfig;
    if (payload.leadViewType) apipayload.leadViewType = payload.leadViewType;

    return axios({
        url: `${API_ENDPOINTS.GET_PORTFOLIO}`,
        method: POST,
        data: apipayload,
        params: {
            ...(payload?.companyName && { companyName: payload.companyName }),
        },
    });
}

export function getPlatformUserDetails(payload: { userId: string }) {
    return axios({
        url: API_ENDPOINTS.GET_PLATFORM_USER_DETAILS,
        method: GET,
        params: {
            userId: payload.userId,
        },
        headers: {
            'x-organization-id': payload.userId,
        },
    });
}

export function getOwnerData(payload: GetOwnerDataPayload) {
    return axios({
        url: API_ENDPOINTS.GET_OWNER_DATA,
        method: GET,
        params: {
            parentOrgId: payload?.investorOrgId,
        },
    });
}

export function getSingleCompanyDetailsService(payload: any) {
    return axios({
        url: API_ENDPOINTS.GET_SINGLE_COMPANY_DATA,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            ...payload,
        },
        headers: {
            'x-organization-id': payload?.assesseeOrgId,
        },
    });
}

export function getCompanyCommentsService(payload: { assesseeOrgId: string; investorOrgId: string }) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_COMPANY_MANDATE_COMMENTS,
        method: POST,
        data: {
            requestPayload: {
                requestId: `FE_COMPANY_COMMENTS_${getTimeStamp()}`,
            },
        },
        headers: {
            ...(payload?.assesseeOrgId && { 'x-requested-organization-id': payload.assesseeOrgId }),
            ...(payload?.investorOrgId && { 'x-organization-id': payload.investorOrgId }),
        },
    });
}

// ─── Paginated comments — HTTP helpers ───────────────────────────────────
//
// Two axios callers consumed by the useCommentsPagination hook:
//   - callLeadComments → Polus /unifier/leadComments
//   - callCommentList  → AICA  /comment/list
//
// Response normalisation, routing, and the merged FE shape live in the hook
// (src/modules/Investor/CommentsThread/useCommentsPagination.ts). Per
// separation of concerns, this file only fires HTTP requests.

export interface PaginationIds {
    assesseeOrgId?: string;
    investorOrgId?: string;
    organizationId?: string;
    dealId?: string;
}

export function callLeadComments(args: { ids: PaginationIds; page: number; size: number; source?: string; searchText?: string }) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_COMPANY_MANDATE_COMMENTS,
        method: POST,
        data: {
            requestPayload: {
                requestId: `FE_PAGINATED_LEAD_COMMENTS_${getTimeStamp()}`,
                page: args.page,
                size: args.size,
                ...(args.source && { source: args.source }),
                ...(args.searchText && { q: args.searchText }),
            },
        },
        headers: {
            ...(args.ids.assesseeOrgId && { 'x-requested-organization-id': args.ids.assesseeOrgId }),
            ...(args.ids.investorOrgId && { 'x-organization-id': args.ids.investorOrgId }),
        },
    });
}

/**
 * GET /comment/list on AICA. Returns Spring Page<CommentEntity>.
 *
 * @param entityType  BE CommentEntityType enum value. Note the FE→BE mapping:
 *                    FE source `INVESTOR_DEAL` → BE entityType `'DEAL'`.
 *                    FE source `MANAGE_PARTNERS_LENDERS` → BE entityType `'LEAD'`.
 *                    FE source `DEAL` → BE entityType `'MANDATE'`.
 * @param entityId    Required string — callers guard for missing ids before invocation.
 */
export function callCommentList(
    entityType: CommentEntityType,
    entityId: string,
    page: number,
    size: number,
    source?: string,
    searchText?: string,
) {
    return axios({
        baseURL: aicaBaseUrl,
        url: API_ENDPOINTS.GET_PAGINATED_COMMENT_LIST,
        method: GET,
        params: { entityType, entityId, page, size, ...(source && { source }), ...(searchText && { q: searchText }) },
    });
}

/**
 * GET /comment/thread on AICA — every comment sharing a Gmail threadId, scoped
 * to one entity. Backs the Phase 2 "View Email" modal's thread navigation.
 */
export function callCommentThread(entityType: CommentEntityType, entityId: string, threadId: string) {
    return axios({
        baseURL: aicaBaseUrl,
        url: API_ENDPOINTS.GET_COMMENT_THREAD,
        method: GET,
        params: { entityType, entityId, threadId },
    });
}

export function initiateReportGenerationService(payload: InitiateReportGenerationPayload) {
    return axios({
        url: API_ENDPOINTS.INITIATE_REPORT_GENERATION,
        method: POST,
        data: {
            assesseeOrgId: payload.assesseeOrgId,
            requestId: getTimeStamp(),
        },
    });
}
export function fetchCityNames() {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_ALL_CITIES,
        method: GET,
    });
}

export function fetchAllUser(payload) {
    return axios({
        url: API_ENDPOINTS.FETCH_ALL_USERS,
        method: 'POST',
        data: {
            pageSize: payload.pageSize,
            pageNumber: payload.pageNumber,
            orgId: payload.orgId,
            userType: payload.userType,
        },
    });
}

export function fetchAllUsersUnified(payload) {
    return axios({
        url: API_ENDPOINTS.GET_ALL_USERS_UNIFIED,
        method: 'POST',
        data: {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            userType: payload.userType,
            globalSearchText: payload.globalSearchText,
        },
    });
}

export function fetchOrgBasedUsers(payload: FetchOrgBasedUsersPayload) {
    return axios({
        url: API_ENDPOINTS.GET_ORG_BASED_USERS,
        method: 'POST',
        data: {
            pageSize: payload?.pageSize,
            pageNumber: payload?.pageNumber,
            organisationId: payload.orgId,
            userType: payload.userType,
            organisationName: payload?.orgName,
            filters: payload?.filters,
            getPaginatedData: payload?.getPaginatedData,
        },
    });
}

export function getAllInvestmentPreferenceData(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_ALL_INVESTMENT_PREFERENCE_DATA,
        method: POST,
        data: payload,
    });
}

export function getAllInvestmentPreferences(payload: FetchAllInvestmentPreferencesPayload) {
    return axios({
        url: API_ENDPOINTS.GET_ALL_INVESTMENT_PREFERENCES,
        baseURL: polusBaseUrl,
        method: POST,
        data: payload,
    });
}

export function fetchAmlPepData(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_AML_PEP_DATA,
        method: GET,
        params: {
            orgId: payload?.orgId,
            requestId: getTimeStamp(),
        },
    });
}

// checkType/identifier/entityName may contain spaces (e.g. "Indian Watchlists") —
// axios's default params serializer percent-encodes them on the wire; polus/UW read decoded.
export function fetchRegulatoryRiskHits(payload: { orgId: string; identifier?: string; entityName?: string; checkType: string }) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_REGULATORY_RISK_HITS,
        method: GET,
        params: {
            orgId: payload?.orgId,
            ...(payload?.identifier ? { identifier: payload.identifier } : { entityName: payload?.entityName }),
            checkType: payload?.checkType,
            requestId: getTimeStamp(),
        },
    });
}

export const callEditUser = (payload) => {
    return axios({
        url: API_ENDPOINTS.EDIT_USER,
        method: POST,
        data: {
            userId: payload.userId,
            orgInfo: {
                userAdditionalDetails: payload.userAdditionalDetails,
            },
        },
    });
};

export function inviteCompanyService(payload: InviteCompanyPayload) {
    return axios({
        url: API_ENDPOINTS.INVITE_COMPANY,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                ...payload,
            },
        },
    });
}
export function inviteCompanyServiceV2(payload: InviteCompanyPayloadV2) {
    return axios({
        url: `${cerberusBaseUrl}${CERBERUS_ENDPOINTS.INVITE_COMPANY_V2}`,
        method: POST,
        data: payload,
    });
}

export function addToWatchlistService(payload: AddCompanyToWatchlistPayload) {
    return axios({
        url: API_ENDPOINTS.ADD_TO_WATCH,
        method: POST,
        data: {
            orgInfo: {
                ...payload,
            },
        },
    });
}

export function getInvestorAccessTypePermissionsService(payload: InvestorAccessTypePermissionServicePayload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                ...payload,
            },
        },
    });
}

export function moveCompanyToNewTierService(payload: AddCompanyToScanPayload) {
    return axios({
        url: API_ENDPOINTS.MOVE_COMPANY_TO_NEW_TIER,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                ...payload,
            },
        },
    });
}

export function inviteCompanyUserService(payload: AddCompanyToScanPayload) {
    return axios({
        url: API_ENDPOINTS.INVITE_COMPANY_USER,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                ...payload,
            },
        },
    });
}

export function setLeadArchivedService(payload: { orgId: string; archived: boolean }) {
    return axios({
        url: API_ENDPOINTS.SET_LEAD_ARCHIVED,
        method: POST,
        data: { orgId: payload.orgId, archived: payload.archived },
    });
}

export function updateCompanyService(payload: CompanyUpdatePayload) {
    // Important: avoid mutating the caller's payload since saga uses it to update Redux immediately
    const dataToPost = {
        ...payload,
        companyComment: payload?.companyComment ? { ...payload.companyComment } : undefined,
        customFields: cleanCustomFields(payload?.customFields || {}),
    };

    if (dataToPost?.companyComment?.additionalData) delete dataToPost.companyComment.additionalData;
    return axios({
        url: API_ENDPOINTS.COMPANY_UPDATE,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: dataToPost,
    });
}

export function createDealService(payload: any) {
    return axios({
        url: API_ENDPOINTS.CREATE_DEAL,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function createInvestmentPreference(payload: any) {
    return axios({
        url: API_ENDPOINTS.CREATE_INVESTMENT_PREFERENCE,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function getMetricsForDealCreationService(payload: FetchDealMetricsPayload) {
    return axios({
        url: API_ENDPOINTS.METRICS_FOR_DEAL_CREATION,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: {
                requestId: getTimeStamp(),
                ...payload,
            },
        },
    });
}

export function getPecMatchingDataService(payload: any) {
    return axios({
        url: API_ENDPOINTS.PEC_MATCHING,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function getAllDealsService(payload: FetchDealsPayload) {
    return axios({
        url: API_ENDPOINTS.GET_ALL_DEALS,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getUniqueRequestId(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function getAllCompanyDealsService(payload: FetchDealsPayload) {
    return axios({
        url: API_ENDPOINTS.GET_ALL_COMPANY_DEALS,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function getAllLenderDealsService(payload: FetchLenderDealsPayload) {
    return axios({
        url: API_ENDPOINTS.GET_ALL_LENDER_DEALS,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getUniqueRequestId(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function getSpecificLenderDealsService(payload: FetchLenderDealsPayload) {
    return axios({
        url: API_ENDPOINTS.GET_SPECIFIC_LENDER_DEALS,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getUniqueRequestId(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function getInvestmentPreference(investmentPreferenceId: string) {
    return axios({
        url: API_ENDPOINTS.GET_INVESTMENT_PREFERENCE,
        baseURL: polusBaseUrl,
        method: GET,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        params: {
            investmentPreferenceId,
        },
    });
}

export function updateInvestmentPreference(payload: any) {
    return axios({
        url: API_ENDPOINTS.UPDATE_INVESTMENT_PREFERENCE,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: payload,
        },
    });
}

export function updateDealService(payload: DealUpdatePayload) {
    const dataToPost = {
        ...payload,
        comment: payload?.comment ? { ...payload.comment } : undefined,
        customFields: cleanCustomFields(payload?.customFields || {}),
    };
    if (dataToPost?.comment?.additionalData) delete dataToPost.comment.additionalData;
    return axios({
        url: API_ENDPOINTS.DEAL_UPDATE,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: dataToPost,
        },
    });
}

export function updateInvestorDealService(payload: InvestorDealUpdatePayload) {
    let dataToPost = JSON.parse(JSON.stringify(payload));
    if (dataToPost?.comment?.additionalData) delete dataToPost.comment.additionalData;
    return axios({
        url: API_ENDPOINTS.INVESTOR_DEAL_UPDATE,
        baseURL: polusBaseUrl,
        method: POST,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        data: {
            requestPayload: {
                dealId: payload.investorDealId,
                dealStatus: payload.dealStatus,
                lastUpdatedBy: payload.lastUpdatedBy,
                comment: dataToPost.comment,
                owner: payload.owner,
                userId: payload.userId,
                dealTerms: payload.dealTerms,
                customFields: cleanCustomFields(payload?.customFields || {}),
                owners: payload.owners,
            },
        },
    });
}

export function unlockAicaForCompanyService(payload: UnlockAICAReportPayload) {
    return axios({
        url: API_ENDPOINTS.UNLOCK_AICA,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                ...payload,
            },
            tabs: payload.tabs,
        },
    });
}

export function getDealDataService(payload: { dealId: string; dealBeneficiaryId?: string }) {
    return axios({
        url: API_ENDPOINTS.GET_DEAL_DATA,
        baseURL: polusBaseUrl,
        method: GET,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        params: {
            dealId: payload?.dealId,
            dealBeneficiaryId: payload?.dealBeneficiaryId,
        },
    });
}

export function getLenderDealDataService(payload: { dealId: string }) {
    return axios({
        url: API_ENDPOINTS.GET_LENDER_DEAL_DATA,
        baseURL: polusBaseUrl,
        method: GET,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        params: {
            dealId: payload.dealId,
        },
    });
}

// Engagement Letter
export function getEngagementLetterTemplateService(payload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                accountType: payload.accountType,
                investorOrgId: payload.pnoId,
                primaryEmail: payload.primaryEmail,
                assesseeOrgId: payload.orgId,
                configName: 'AICA_ENGAGEMENT_LETTER_TEMPLATE',
            },
        },
    });
}

export function getBureauKycConsentTemplateService(payload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                accountType: payload.accountType,
                investorOrgId: payload.investorOrgId,
                primaryEmail: payload.primaryEmail,
                assesseeOrgId: payload.assesseeOrgId,
                configName: 'AICA_BUREAU_KYC_CONSENT_TEMPLATE',
            },
        },
    });
}

// NDA
export function getNDATemplateService(payload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                accountType: payload.accountType,
                investorOrgId: payload.pnoId,
                primaryEmail: payload.primaryEmail,
                assesseeOrgId: payload.orgId,
                configName: 'AICA_NDA_TEMPLATE',
            },
        },
    });
}

// Application Form
export function getApplicationFormTemplateService(payload) {
    return axios({
        url: API_ENDPOINTS.GET_CONFIG,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                accountType: payload.accountType,
                investorOrgId: payload.pnoId,
                primaryEmail: payload.primaryEmail,
                assesseeOrgId: payload.orgId,
                configName: 'AICA_APPLICATION_FORM_TEMPLATE',
            },
        },
    });
}

export function getInvestmentReportService(payload) {
    return axios({
        url: API_ENDPOINTS.GET_INVESTMENT_REPORT,
        method: GET,
        params: {
            requestId: getTimeStamp(),
            assesseeOrgId: payload?.assesseeOrgId,
        },
    });
}

export function getTabLockStatus(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_TAB_LOCK_STATUS,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            ...payload,
        },
        params: {
            assesseeOrgId: payload?.assesseeOrgId,
        },
    });
}

export function fetchAvailableProducts(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_AVAILABLE_PRODUCTS,
        method: POST,
        data: {
            requestPayload: {
                ...payload,
            },
        },
    });
}

export function fetchCompanySector() {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_COMPANY_SECTOR,
        method: GET,
        params: {
            requestId: getTimeStamp(),
        },
    });
}

export function fetchAvailableDebtProducts(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_ALL_PRODUCTS,
        method: GET,
        params: {
            ...payload,
        },
    });
}

export function fetchOrgUser(payload: { orgId: string; accountType: string }) {
    return axios({
        url: API_ENDPOINTS.GET_ORG_USER,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            orgInfo: {
                orgId: payload.orgId,
                accountType: payload.accountType,
            },
        },
    });
}

export function getAllTaskTrackerDataService(payload: { orgId: string }) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_ALL_TASK_TRACKER_DATA,
        method: GET,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
        },
        params: {
            requestId: getTimeStamp(),
            orgId: payload.orgId,
        },
    });
}

export function verifyWebsite(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.VERIFY_WEBSITE,
        method: POST,
        data: {
            requestPayload: {
                requestId: getTimeStamp(),
                ...payload,
            },
        },
    });
}
//# removed params as it as  getting requestid as timestamp which was added in headers
export function fetchOrgInfoService(payload: { orgId: string }) {
    return axios({
        url: API_ENDPOINTS.GET_ORG_INFO,
        method: GET,
        headers: {
            [REQUEST_ID_HEADER]: getTimeStamp(),
            'x-organization-id': payload.orgId,
        },
    });
}

export function getMcaDocApiData(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_MCA_API_DATA,
        method: GET,
        params: {
            requestId: getTimeStamp(),
            orgId: payload.orgId,
        },
    });
}

export function getCombinedOrgUsers(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_COMBINED_ORG_USERS,
        method: GET,
        params: {
            companyOrgId: payload.companyOrgId,
            partnerOrgId: payload.partnerOrgId,
        },
    });
}

export function getCombinedDealUsers(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_COMBINED_DEAL_USERS,
        method: GET,
        params: {
            companyOrgId: payload.companyOrgId,
            dealId: payload.dealId,
        },
    });
}

export function sendSystemNotification(payload) {
    return axios({
        baseURL: aicaBaseUrl,
        url: API_ENDPOINTS.SEND_SYSTEM_NOTIFICATION,
        method: POST,
        data: { ...payload },
    });
}

export function getNotificationTemplate(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_NOTIFICATION_TEMPLATE,
        method: GET,
        params: {
            templateId: payload.templateId,
        },
    });
}

export function getFormerEntityName(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_FORMER_ENTITY_NAME,
        method: GET,
        params: {
            organizationId: payload.organizationId,
        },
    });
}

export function getDealsByStatus(payload: { orgId: string; status: string[] }) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_DEALS_BY_STATUS,
        method: POST,
        headers: {
            'x-requested-organization-id': payload?.orgId,
        },
        data: {
            requestPayload: {
                status: payload?.status,
            },
        },
    });
}

export function getMandateQualificationChecks(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_MANDATE_WUALIFICATION_CHECKS,
        method: POST,
        headers: {
            'x-requested-organization-id': payload?.orgId,
            'x-organization-id': payload?.investorOrgId,
        },
        data: {
            requestPayload: {
                requestId: getTimeStamp(),
                dealId: payload.dealId,
            },
        },
    });
}

export function editUserV2(payload: EditUserV2Payload) {
    return axios({
        baseURL: aicaBaseUrl,
        url: API_ENDPOINTS.EDIT_USER_V2,
        method: POST,
        data: payload,
    });
}

export function bulkAddService(payload: BulkAddPayload) {
    return axios({
        baseURL: aicaBaseUrl,
        url: API_ENDPOINTS.BULK_ADD,
        method: POST,
        data: payload,
    });
}

export function fetchLenderSummary(payload: { pnoId: string }) {
    return axios({
        url: API_ENDPOINTS.LENDER_SUMMARY,
        method: GET,
        params: { pnoId: payload.pnoId },
    });
}

export function fetchNotificationsService(payload: { organisationUserId: string; pageNum: number; pageSize: number }) {
    return axios({
        url: API_ENDPOINTS.GET_NOTIFICATIONS,
        method: POST,
        data: {
            organisationUserId: payload.organisationUserId,
            pageNum: payload.pageNum,
            pageSize: payload.pageSize,
        },
    });
}

export function markAllNotificationsReadService(payload: { organisationUserId: string }) {
    return axios({
        url: API_ENDPOINTS.MARK_ALL_NOTIFICATIONS_READ,
        method: POST,
        data: {
            organisationUserId: payload.organisationUserId,
        },
    });
}

export function markNotificationReadService(payload: { organisationUserId: string; notificationId: string }) {
    return axios({
        url: API_ENDPOINTS.MARK_NOTIFICATION_READ,
        method: POST,
        data: {
            organisationUserId: payload.organisationUserId,
            notificationId: payload.notificationId,
        },
    });
}

export function markNotificationUnreadService(payload: { organisationUserId: string; notificationId: string }) {
    return axios({
        url: API_ENDPOINTS.MARK_NOTIFICATION_UNREAD,
        method: POST,
        data: {
            organisationUserId: payload.organisationUserId,
            notificationId: payload.notificationId,
        },
    });
}

export function fetchLenderCompanyList(payload: { lenderName: string; pnoId: string }) {
    return axios({
        url: API_ENDPOINTS.LENDER_COMPANY_LIST,
        method: GET,
        params: { lenderName: payload.lenderName, pnoId: payload.pnoId },
    });
}

export function getMeetingTranscriptService(payload: FetchMeetingTranscriptPayload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_MEETING_TRANSCRIPT,
        method: GET,
        params: {
            recordingId: payload.recordingId,
            orgId: payload.orgId,
            requestId: getTimeStamp(),
        },
    });
}

const API_ENDPOINTS = {
    GET_CONFIG: '/account/config',
    EDIT_USER_V2: '/account/editUserDetails',
    GET_PORTFOLIO: '/portfolio/investor/fetch',
    GET_OWNER_DATA: '/portfolio/getPnoOwners',
    GET_SINGLE_COMPANY_DATA: '/portfolio/assessee/basic/fetch',
    GET_ORG_INFO: '/account/fetch/orgInfo',
    INITIATE_REPORT_GENERATION: '/profile/evaluation/generate',
    INVITE_COMPANY: '/account/org/create',
    COMPANY_UPDATE: '/account/updateOrgMaster',
    CREATE_DEAL: '/unifier/createDeal',
    METRICS_FOR_DEAL_CREATION: '/unifier/getAicaDealMetrics',
    PEC_MATCHING: '/unifier/pecMatching',
    DEAL_UPDATE: '/unifier/updateDeal',
    INVESTOR_DEAL_UPDATE: '/unifier/updateInvestorDeal',
    GET_ALL_DEALS: '/unifier/getAllDeals',
    GET_ALL_COMPANY_DEALS: '/unifier/getAllCompanyDeals',
    GET_ORG_BASED_USERS: '/account/getOrgBasedUsers',
    GET_ALL_LENDER_DEALS: '/unifier/getAllLenderDeals',
    GET_SPECIFIC_LENDER_DEALS: '/unifier/getSpecificLenderDeals',
    GET_INVESTMENT_PREFERENCE: '/unifier/getInvestmentPreference',
    GET_ALL_INVESTMENT_PREFERENCES: '/unifier/getAllInvestmentPreferneces',
    UPDATE_INVESTMENT_PREFERENCE: '/unifier/investmentPreference/update',
    CREATE_INVESTMENT_PREFERENCE: '/unifier/investmentPreference/create',
    GET_DEAL_DATA: '/unifier/getDealData',
    GET_LENDER_DEAL_DATA: '/unifier/getLenderDealData',
    GET_INVESTMENT_REPORT: '/investmentReport/download',
    GET_COMBINED_ORG_USERS: '/unifier/getCombinedOrgUsers',
    GET_COMBINED_DEAL_USERS: '/unifier/getCombinedDealUsers',
    GET_DEALS_BY_STATUS: '/unifier/getDealsByStatus',
    GET_COMPANY_MANDATE_COMMENTS: '/unifier/leadComments',
    GET_PAGINATED_COMMENT_LIST: '/comment/list',
    GET_COMMENT_THREAD: '/comment/thread',
    GET_MANDATE_WUALIFICATION_CHECKS: '/unifier/getMandateQualificationChecks',
    GET_ALL_CITIES: '/unifier/getCitiesList',

    ADD_TO_WATCH: '/account/org/addWatchAccount',
    MOVE_COMPANY_TO_NEW_TIER: '/account/org/moveCompanyToNewTier',
    INVITE_COMPANY_USER: '/account/org/inviteCompanyOrgUser',
    EDIT_USER: '/account/editUser',
    FETCH_ALL_USERS: '/account/getAllUsers',
    GET_ALL_USERS_UNIFIED: '/account/getAllUsersUnified',
    UNLOCK_AICA: '/account/unlock',
    GET_TAB_LOCK_STATUS: '/insights/getTabsLockStatus',
    GET_ALL_INVESTMENT_PREFERENCE_DATA: '/unifier/getAllInvestmentPreferenceData',
    GET_AVAILABLE_PRODUCTS: '/unifier/getAvailableProducts',
    GET_ALL_PRODUCTS: '/unifier/getAllProducts',
    GET_COMPANY_SECTOR: '/unifier/getCompanySector',
    GET_ALL_TASK_TRACKER_DATA: '/unifier/getAllTaskTrackerData',
    GET_ORG_USER: '/account/getOrgUser',
    GET_MCA_API_DATA: '/unifier/getMcaDocApiData',
    SEND_SYSTEM_NOTIFICATION: '/dataGap/sendSystemNotification',
    GET_NOTIFICATION_TEMPLATE: '/unifier/getNotificationTemplate',

    GET_AML_PEP_DATA: '/unifier/getAmlPepData',
    GET_REGULATORY_RISK_HITS: '/unifier/getRegulatoryRiskHits',
    VERIFY_WEBSITE: '/unifier/websiteVerification',
    GET_FORMER_ENTITY_NAME: '/unifier/getFormerEntityName',
    GET_PLATFORM_USER_DETAILS: '/account/getPlatformUserDetails',

    // LEAD ARCHIVE
    SET_LEAD_ARCHIVED: '/portfolio/lead/setArchived',

    //BULK_ADD
    BULK_ADD: '/account/org/bulk-add',

    // LENDER NETWORK
    LENDER_SUMMARY: '/profile/lender-connections/lender-summary',
    LENDER_COMPANY_LIST: '/profile/lender-connections/company-list',

    // NOTIFICATION CENTER
    GET_NOTIFICATIONS: '/notification/get-notifications',
    MARK_ALL_NOTIFICATIONS_READ: '/notification/mark-all-read',
    MARK_NOTIFICATION_READ: '/notification/mark-read',
    MARK_NOTIFICATION_UNREAD: '/notification/mark-unread',

    // LOOKALIKES
    GET_LOOKALIKES: '/unifier/getLookalikes',
    RECOMPUTE_LOOKALIKES: '/unifier/recomputeLookalikes',
    BATCH_ORG_INFO: '/unifier/fetch/batchOrgInfo',

    // GENERATED DOCUMENTS (Config-Driven Reports Engine)
    LIST_DOCUMENTS: '/profile/document/list',
    GENERATE_DOCUMENT: '/unifier/document/generate',
    SAVE_DOCUMENT: '/profile/document/save',
    GET_DOCUMENT: '/profile/document/get',
    GET_DOCUMENT_HISTORY: '/profile/document/history',
    DELETE_DOCUMENT: '/profile/document/delete',
    ARCHIVE_DOCUMENT: '/profile/document/archive',
    UNARCHIVE_DOCUMENT: '/profile/document/unarchive',

    // Meeting Transcript
    GET_MEETING_TRANSCRIPT: '/unifier/getMeetingTranscript',
};

const CERBERUS_ENDPOINTS = {
    INVITE_COMPANY_V2: '/v2/access/signUp',
};

const REQUEST_ID_HEADER = 'request-id';

type OwnerType = {
    name: string;
    userId: string;
    activityBy?: string;
    activityDate?: string;
    ownerType?: string;
};

// Tri-state sent to addCompanyToWatchlist to resolve which BE branch handles
// the picked card. See AddCompanyToWatchlistPayload.addAsIndividualPan.
export const ADD_AS_INDIVIDUAL_PAN = {
    INDIVIDUAL: 'INDIVIDUAL',
    COMPANY: 'COMPANY',
    PROPRIETOR: 'PROPRIETOR',
} as const;

export type AddAsIndividualPan = (typeof ADD_AS_INDIVIDUAL_PAN)[keyof typeof ADD_AS_INDIVIDUAL_PAN];

export type AddCompanyToWatchlistPayload = {
    websiteUrl?: string;
    investorOrgId: string;
    journeyName: string;
    inviteCode?: string;
    identifier?: string;
    brandName?: string;
    isPickedFromCompaniesList?: boolean;
    companyDetails?: Record<string, any>;
    // Raw selection from Global Search Add New (for analytics/debug on BE)
    searchResultContext?: Record<string, any>;
    // Set when user explicitly picks a card for a person PAN in global search:
    //   "INDIVIDUAL"  → routes BE to populateIndividualDetails branch (skip entity-search)
    //   "COMPANY"     → routes BE to entity-search company (bypass DIN/GST auto-decision)
    //   "PROPRIETOR"  → routes BE to the sole-proprietor entity-search branch
    // Omit for BE auto-decision (4-branch DIN/GST routing).
    addAsIndividualPan?: AddAsIndividualPan;
    // Entity-search card id for the picked row (sole-proprietor or company), so BE
    // resolves the exact entity instead of re-deciding among same-name candidates.
    selectedEntityId?: string;
};

export type InvestorAccessTypePermissionServicePayload = {
    accountType: string;
    investorOrgId: string;
    primaryEmail: string;
    reportId?: string;
    configName: string;
};

export type AddCompanyToScanPayload = {
    assesseeOrgId: string;
    investorOrgId?: string;
    phoneNumber?: string;
    countryCode?: string;
    countryId?: string;
    primaryEmail?: string;
    inviteType?: string;
    triggerNotification?: boolean;
    primaryUserName?: string;
    websiteUrl?: string;
    pan?: string;
};

export enum CompanyStatus {
    NEW = 'NEW',
    ONGOING = 'ONGOING',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
    DEAL_CREATED = 'DEAL_CREATED',
}

export type CompanyUpdatePayload = {
    source?: string | null;
    orgId: string;
    companyStatus?: {
        status?: string;
        userId: string;
        activityDate: number;
    };
    companyRiskGrade?: RiskGrade;
    monitoringStatus?: MonitoringStatus;
    companyComment?: {
        text: string;
        activityBy: string;
        userId?: string;
        activityDate: string;
        additionalData?: Object;
    };
    owner?: OwnerType;
    owners?: OwnerType[];
    engagementLetterStatus?: EngagementLetterStatus;
    customFields?: { [key: string]: any };
};

export type FetchDealMetricsPayload = {
    requestId?: string;
    parentOrgId: string;
    orgId: string;
    startDate: string;
    endDate: string;
};

export type FetchDealsPayload = {
    dealStatuses: string[];
    pageNumber: number;
    pageSize: number;
    ownerUserId: string;
    dealBeneficiaryId?: string;
    sortConfig?: SortConfig;
    filters?: {
        dealBeneficiaryIds?: string[];
    };
};

export type FetchLenderDealsPayload = {
    dealProposerId: string;
    dealStatuses?: string[];
    dealReceiverId?: string;
    pageNumber: number;
    pageSize: number;
    ownerUserId: string;
    sortConfig?: SortConfig;
    filters?: Record<string, any>;
};

export type DealUpdatePayload = {
    dealId: string;
    dealStatus?: string;
    lastUpdatedBy: string;
    comment?: {
        text: string;
        activityBy: string;
        userId: string;
        activityDate: string;
        additionalData?: any;
    };
    customFields?: { [key: string]: any };
};

export type InvestorDealUpdatePayload = {
    dealId?: string;
    source?: string;
    investorDealId?: string;
    dealStatus?: string;
    lastUpdatedBy: string;
    comment?: {
        text: string;
        activityBy: string;
        userId: string;
        activityDate: string;
        additionalData?: any;
    };
    owner?: OwnerType;
    owners?: OwnerType[];
    userId?: string;
    selectedCategory?: string;
    dealTerms?: {
        [key: string]: any;
    };
    customFields?: { [key: string]: any };
    isLenderSideUsers?: boolean;
    selectedLenderUsers?: string[];
    allUserOptions?: Option[];
};

export type UnlockAICAReportPayload = {
    investorOrgId: string;
    assesseeOrgId: string;
    tabs: string[];
};

export type FetchInvestmentPreferencesPayload = {
    aggregatorId: string;
    lenderIds: string[];
};

export type FetchAllInvestmentPreferencesPayload = {
    requestPayload: {
        aggregatorId: string;
        lenderIds: string[];
        investmentPreferenceType: string;
        investmentPreferenceId: string;
    };
};

export type EditUserV2Payload = {
    userDetails: {
        userId: string;
        name?: string;
        emailId?: string;
        phoneNumber?: string;
        customFields?: { [key: string]: any };
        isDeleted?: boolean;
        role?: string;
        userType?: string;
    };
    triggerNotification?: boolean;
};

export interface BulkAddPayload {
    userName: string;
    userEmail: string;
    pnoId: string;
    pnoName: string;
    bulkInputText: string;
}

// ── Lookalike services (via polus) ───────────────────────────────────────────

export function getLookalikesService(orgId: string) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_LOOKALIKES,
        method: GET,
        params: { orgId },
    });
}

export function recomputeLookalikesService(orgId: string) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.RECOMPUTE_LOOKALIKES,
        method: POST,
        params: { orgId },
    });
}

export function fetchBatchOrgInfoService(assesseeOrgIdList: string[]) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.BATCH_ORG_INFO,
        method: POST,
        data: { requestPayload: { assesseeOrgIdList } },
    });
}

// Generated Documents (Config-Driven Reports Engine)

export function listDocumentsService(payload: { assesseeOrgId: string; includeArchived?: boolean }) {
    return axios({
        url: API_ENDPOINTS.LIST_DOCUMENTS,
        method: GET,
        params: {
            assesseeOrgId: payload.assesseeOrgId,
            ...(payload.includeArchived !== undefined ? { includeArchived: payload.includeArchived } : {}),
        },
        headers: {
            'x-requested-organization-id': payload.assesseeOrgId,
        },
    });
}

export function composeDocumentService(payload: {
    assesseeOrgId: string;
    documentType: string;
    investorOrgId?: string;
    [key: string]: any;
}) {
    const { assesseeOrgId, documentType, investorOrgId, ...rest } = payload;
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GENERATE_DOCUMENT,
        method: POST,
        // Unifier accepts only `requestPayload`; orgId must live inside it so the
        // GENERATE_DOCUMENT ServiceDefinition's ${orgId} + buildComposeInput resolve.
        // `name` is required: FETCH_CONFIG_POLUS resolves its `${name}` query param
        // from this payload to look up the template config — the documentType value
        // (e.g. ONE_PAGER) IS the template config name.
        data: { requestPayload: { requestId: getTimeStamp(), orgId: assesseeOrgId, name: documentType, ...rest } },
        headers: {
            'x-requested-organization-id': assesseeOrgId,
            // FETCH_CONFIG_POLUS resolves the template's ViewConfig by the PNO org from
            // the x-organization-id header (NOT the assessee/company). Without this the
            // interceptor's default (the company) is used and the config falls back to
            // DEFAULT. Mirrors the other PNO-config services in this file.
            ...(investorOrgId && { 'x-organization-id': investorOrgId }),
        },
    });
}

export function saveDocumentService(payload: { assesseeOrgId: string; [key: string]: any }) {
    const { assesseeOrgId, ...rest } = payload;
    return axios({
        url: API_ENDPOINTS.SAVE_DOCUMENT,
        method: POST,
        // assesseeOrgId must be in the BODY: DocumentController binds it from
        // @RequestBody DocumentRequest.assesseeOrgId, not the header. Omitting it
        // persisted documents with a null assesseeOrgId, so the list query
        // (findByAssesseeOrgId...) never matched them and the table showed empty.
        data: { requestId: getTimeStamp(), assesseeOrgId, ...rest },
        headers: {
            'x-requested-organization-id': assesseeOrgId,
        },
    });
}

export function getDocumentService(payload: { assesseeOrgId: string; docId?: string; genId?: string; documentType?: string }) {
    return axios({
        url: API_ENDPOINTS.GET_DOCUMENT,
        method: GET,
        // Controller binds `id` (exact row), `genId` (lineage → newest live row,
        // draft preferred) or `assesseeOrgId`+`documentType` (org's newest live doc
        // of the type — the drawers' open behaviour); sending `docId` 400s before
        // the handler.
        params: {
            ...(payload.docId ? { id: payload.docId } : {}),
            ...(payload.genId ? { genId: payload.genId } : {}),
            ...(payload.documentType ? { assesseeOrgId: payload.assesseeOrgId, documentType: payload.documentType } : {}),
        },
        headers: {
            'x-requested-organization-id': payload.assesseeOrgId,
        },
    });
}

export function getDocumentHistoryService(payload: { assesseeOrgId: string; genId: string }) {
    return axios({
        url: API_ENDPOINTS.GET_DOCUMENT_HISTORY,
        method: GET,
        params: { genId: payload.genId, assesseeOrgId: payload.assesseeOrgId },
        headers: {
            'x-requested-organization-id': payload.assesseeOrgId,
        },
    });
}

export function deleteDocumentService(payload: { assesseeOrgId: string; genId: string }) {
    return axios({
        url: API_ENDPOINTS.DELETE_DOCUMENT,
        method: POST,
        data: { genId: payload.genId, assesseeOrgId: payload.assesseeOrgId },
        headers: {
            'x-requested-organization-id': payload.assesseeOrgId,
        },
    });
}

export function archiveDocumentService(payload: { assesseeOrgId: string; genId: string }) {
    return axios({
        url: API_ENDPOINTS.ARCHIVE_DOCUMENT,
        method: POST,
        data: { genId: payload.genId, assesseeOrgId: payload.assesseeOrgId },
        headers: {
            'x-requested-organization-id': payload.assesseeOrgId,
        },
    });
}

export function unarchiveDocumentService(payload: { assesseeOrgId: string; genId: string }) {
    return axios({
        url: API_ENDPOINTS.UNARCHIVE_DOCUMENT,
        method: POST,
        data: { genId: payload.genId, assesseeOrgId: payload.assesseeOrgId },
        headers: {
            'x-requested-organization-id': payload.assesseeOrgId,
        },
    });
}
